import Vue from "vue";
import { LmgService } from "../resource";

// Memoization cache for catalogAncestors, keyed by `${id}@${treeNonce}`.
// Lives outside state so it never becomes reactive.
const ancestorCache = new Map();

const InsuranceModule = {
  namespaced: true,
  state: {
    // ── V1 page state (do not remove) ──
    datas: [],
    uppers: [],
    data: {},
    // ── Catalog V2 state ──
    catalogRows: [], // raw ResInsuranceData[] — single source of truth for the tree
    treeNonce: 0, // bump on any local mutation to force getter recompute
  },
  mutations: {
    // ── V1 mutations ──
    SET_DATAS(state, payload) {
      state.datas = payload;
    },
    SET_DATA(state, payload) {
      state.data = payload;
    },
    SET_UPPERS(state, payload) {
      state.uppers = payload;
    },
    // ── Catalog V2 mutations (each bumps treeNonce) ──
    SET_CATALOG(state, payload) {
      state.catalogRows = Array.isArray(payload) ? payload : [];
      state.treeNonce++;
    },
    PATCH_ADD(state, node) {
      if (node) {
        state.catalogRows.push(node);
        state.treeNonce++;
      }
    },
    PATCH_UPDATE(state, node) {
      if (!node) return;
      const idx = state.catalogRows.findIndex((r) => r.ID === node.ID);
      if (idx !== -1) {
        // Merge so callers can patch a subset of fields.
        Vue.set(state.catalogRows, idx, { ...state.catalogRows[idx], ...node });
      }
      state.treeNonce++;
    },
    PATCH_REMOVE(state, id) {
      const idx = state.catalogRows.findIndex((r) => r.ID === id);
      if (idx !== -1) {
        state.catalogRows.splice(idx, 1);
      }
      state.treeNonce++;
    },
  },
  actions: {
    // ── V1 actions (unchanged) ──
    createData({ commit, state }, payload) {
      return LmgService.InsuranceCreate(payload);
    },
    updateData({ commit, state }, payload) {
      return LmgService.InsuranceUpdate(payload.ID, payload);
    },
    deleteData({ commit, state }, payload) {
      return LmgService.InsuranceDelete(payload.ID, payload);
    },

    async listUpper({ commit, state }, payload) {
      try {
        const data = await LmgService.InsuranceListUpper(payload);
        if (data.data.Data == null) {
          commit("SET_UPPERS", []);
        } else {
          commit("SET_UPPERS", data.data.Data);
        }
      } catch (err) {
        commit("SET_UPPERS", []);
      }
    },
    async listData({ commit, state }, payload) {
      try {
        const data = await LmgService.InsuranceList(payload);
        if (data.data.Data == null) {
          commit("SET_DATAS", []);
        } else {
          commit("SET_DATAS", data.data.Data);
        }
      } catch (err) {
        commit("SET_DATAS", []);
      }
    },
    async listAll({ commit, state }, payload) {
      try {
        const data = await LmgService.InsuranceListUpper({ level: 0 });
        if (data.data.Data == null) {
          commit("SET_DATAS", []);
        } else {
          commit("SET_DATAS", data.data.Data);
        }
      } catch (err) {
        commit("SET_DATAS", []);
      }
    },

    // ── Catalog V2 actions ──

    // Load the full catalog tree. Pass { includeInactive: true } to also pull
    // is_active=0 rows (default = active-only, unchanged backend behaviour).
    async loadCatalog({ commit }, { includeInactive = false } = {}) {
      const query = { level: 0 };
      if (includeInactive) query.includeInactive = 1;
      try {
        const res = await LmgService.InsuranceCatalog(query);
        const rows = res && res.data && res.data.Data ? res.data.Data : [];
        commit("SET_CATALOG", rows);
        return rows;
      } catch (err) {
        commit("SET_CATALOG", []);
        throw err;
      }
    },

    // Create a node. POST returns no id, so we MUST refetch to learn the new
    // node's ID/ShowName. Preserves the current includeInactive view.
    async createNode(
      { dispatch },
      { name, detail, level, upperID, dataType, includeInactive = false }
    ) {
      const res = await LmgService.InsuranceCreate({
        name,
        detail,
        level,
        upperID,
        dataType,
      });
      await dispatch("loadCatalog", { includeInactive });
      return res;
    },

    // Update mutable fields. Caller MUST pass the FULL {name, detail, dataType,
    // isActive} echoing current values — PUT corrupts the row otherwise.
    async updateNode(
      { commit },
      { id, name, detail, dataType, isActive }
    ) {
      const res = await LmgService.InsuranceUpdate(id, {
        name,
        detail,
        dataType,
        isActive,
      });
      // Optimistic local patch + nonce bump for instant tree refresh.
      commit("PATCH_UPDATE", { ID: id, Name: name, Detail: detail, DataType: dataType, IsActive: isActive });
      return res;
    },

    // Soft-delete (deactivate) via the GUARDED DELETE endpoint. Never use PUT
    // is_active=0 — that bypasses the orphan/in-use guards.
    // On a guard block the backend returns error keys: insurance_is_upper /
    // insurance_is_using — re-thrown so the page can show the reason.
    async deactivateNode({ commit }, { id }) {
      const res = await LmgService.InsuranceDelete(id, {});
      // Mark inactive locally (keeps the node in the tree so it isn't dropped).
      commit("PATCH_UPDATE", { ID: id, IsActive: 0 });
      return res;
    },

    // Re-activate via PUT with isActive=1 + the full echoed fields.
    async reactivateNode({ commit }, { id, name, detail, dataType }) {
      const res = await LmgService.InsuranceUpdate(id, {
        name,
        detail,
        dataType,
        isActive: 1,
      });
      commit("PATCH_UPDATE", { ID: id, Name: name, Detail: detail, DataType: dataType, IsActive: 1 });
      return res;
    },
  },
  getters: {
    // ── V1 getters (unchanged) ──
    getData(state) {
      return state.datas;
    },
    getUpper(state) {
      return state.uppers;
    },
    getDataWithAll(state) {
      return [{ ID: 0, Name: "ทั้งหมด" }, ...state.datas];
    },

    // ── Catalog V2 getters ──

    // Map<ID, node>
    catalogById(state) {
      const m = new Map();
      for (const row of state.catalogRows) {
        m.set(row.ID, row);
      }
      return m;
    },

    // Map<UpperID, node[]> — key 0 holds the roots. O(n) single pass.
    catalogChildrenOf(state) {
      const m = new Map();
      for (const row of state.catalogRows) {
        const key = row.UpperID;
        if (!m.has(key)) m.set(key, []);
        m.get(key).push(row);
      }
      return m;
    },

    // Root nodes (UpperID === 0).
    catalogRoots(state) {
      return state.catalogRows.filter((r) => r.UpperID === 0);
    },

    // (id) => node[]  root→node ancestor path. Memoized by id + treeNonce.
    catalogAncestors(state, getters) {
      return (id) => {
        const cacheKey = `${id}@${state.treeNonce}`;
        if (ancestorCache.has(cacheKey)) {
          return ancestorCache.get(cacheKey);
        }
        // Bound the cache so stale nonce entries can't leak on long sessions.
        if (ancestorCache.size > 2000) ancestorCache.clear();
        const byId = getters.catalogById;
        const path = [];
        const seen = new Set(); // cycle guard
        let node = byId.get(id);
        while (node && node.UpperID !== 0 && !seen.has(node.ID)) {
          seen.add(node.ID);
          const parent = byId.get(node.UpperID);
          if (!parent) break; // orphaned chain — stop
          path.unshift(parent);
          node = parent;
        }
        ancestorCache.set(cacheKey, path);
        return path;
      };
    },

    // Nodes whose UpperID points at a missing parent — the "Unlinked" bucket.
    // Financial safety: these must never be silently dropped from the UI.
    catalogOrphans(state, getters) {
      const byId = getters.catalogById;
      return state.catalogRows.filter(
        (r) => r.UpperID !== 0 && !byId.has(r.UpperID)
      );
    },
  },
};

export default InsuranceModule;
